/**
 * MARCA DA REVENDA — nome e logo no lugar dos da plataforma.
 *
 * Pedido do Jhou em 31/10/2026: *"cada revendedor pode colocar a marca dele
 * no lugar da marca da CDW Mail ... quando eu cadastrar um novo revendedor,
 * ele poder fazer as alterações dele lá. No caso, seria só a marca mesmo"*.
 *
 * O gestor de revenda edita a DELE — a tela nem oferece escolha. O super
 * admin escolhe qual revenda, para poder configurar por quem pedir ajuda.
 * Quem recusa revenda alheia é a API (404), não esta tela.
 *
 * A PRÉVIA É O COMPONENTE DE VERDADE (DesenhoDaMarca), não um desenho
 * parecido: o que se vê aqui é o que a barra de cima e a tela de entrar vão
 * mostrar, inclusive o teto de largura de logo comprido.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { revendas } from "../api/admin";
import * as marcaAPI from "../api/marca";
import type { MarcaDaRevenda as Dados } from "../api/marca";
import { explicar } from "../api/problema";
import { useSessao } from "../sessao/contexto";
import { Icone } from "../ui/Icone";
import { DesenhoDaMarca, iconeDaInicial } from "../ui/Marca";
import { dataCurta } from "../ui/formato";
import { Botao, Cabecalho, Estado } from "./comuns";

// Os mesmos números da API (marca.go). Conferir aqui é só para responder na
// hora; quem decide é o servidor, olhando os bytes.
const NOME_MAXIMO = 40;
const LOGO_MAXIMO = 256 * 1024;
const LARGURA = [16, 2000] as const;
const ALTURA = [16, 1000] as const;

type EstadoDoLogo =
  | { estado: "mantido" }
  | { estado: "removido" }
  | { estado: "novo"; dataURL: string; arquivo: string };

export function MarcaRevenda() {
  const { sessao } = useSessao();
  const superAdmin = sessao?.nivel === "super_admin";
  const [params, setParams] = useSearchParams();

  // A da conta diz QUAL revenda é a do gestor: o login não devolve esse uuid.
  const conta = useQuery({ queryKey: ["marca", "conta"], queryFn: marcaAPI.daConta });
  const todas = useQuery({
    queryKey: ["p", "revendas", "para-marca"],
    queryFn: () => revendas.listar({ por_pagina: 100 }),
    enabled: superAdmin,
  });

  const uuid = (superAdmin && params.get("revenda")) || conta.data?.revenda_uuid || "";
  const atual = useQuery({
    queryKey: ["p", "marca", uuid],
    queryFn: () => marcaAPI.daRevenda(uuid),
    enabled: !!uuid,
  });

  return (
    <div className="max-w-3xl">
      <Cabecalho
        titulo="Marca"
        descricao="O nome e o logo que os clientes desta revenda veem no lugar da marca CDW Mail."
      />

      {superAdmin && (
        <label className="mb-5 block text-sm text-tinta-fraca">
          Revenda
          <select
            value={uuid}
            onChange={(e) => setParams({ revenda: e.target.value })}
            className="mt-1 block w-full max-w-sm rounded-lg border border-linha bg-white px-3 py-2 text-sm text-tinta"
          >
            {!uuid && <option value="">escolha…</option>}
            {(todas.data?.itens ?? []).map((r) => (
              <option key={r.uuid} value={r.uuid}>
                {r.nome}
              </option>
            ))}
          </select>
        </label>
      )}

      {!uuid && !conta.isPending && !superAdmin ? (
        // Admin de domínio que digitou o endereço: o menu não mostra o item,
        // e a API recusaria. Dizer de quem é a marca poupa a pergunta.
        <p className="text-sm text-tinta-media">
          A marca é definida pelo gestor da revenda que atende este domínio.
        </p>
      ) : (
        <Estado consulta={uuid ? atual : conta}>
          {/* key: trocar de revenda recomeça o formulário em vez de herdar o
              que foi digitado para a anterior. */}
          {atual.data && <Formulario key={uuid} dados={atual.data} />}
        </Estado>
      )}
    </div>
  );
}

function Formulario({ dados }: { dados: Dados }) {
  const fila = useQueryClient();
  const [nome, setNome] = useState(dados.padrao ? "" : dados.nome);
  const [logo, setLogo] = useState<EstadoDoLogo>({ estado: "mantido" });
  const [erro, setErro] = useState("");
  const [recado, setRecado] = useState("");
  const [confirmando, setConfirmando] = useState(false);

  const nomeSalvo = dados.padrao ? "" : dados.nome;
  const sujo = nome.trim() !== nomeSalvo || logo.estado !== "mantido";
  const nomeLimpo = nome.trim();
  const nomeInvalido =
    nomeLimpo === "" ? "Escreva o nome." : [...nomeLimpo].length > NOME_MAXIMO ? `Até ${NOME_MAXIMO} caracteres.` : "";

  // O que a prévia mostra: o estado da tela, não o salvo.
  const logoNaPrevia =
    logo.estado === "novo" ? logo.dataURL : logo.estado === "removido" ? null : dados.padrao ? null : dados.logo_url;
  const previa =
    dados.padrao && nomeLimpo === "" && logo.estado !== "novo"
      ? { nome: "CDW Mail", logo_url: null, padrao: true }
      : { nome: nomeLimpo || dados.revenda_nome, logo_url: logoNaPrevia, padrao: false };

  const depoisDeMudar = (r: Dados | undefined) => {
    setErro("");
    setConfirmando(false);
    if (r) fila.setQueryData(["p", "marca", dados.revenda_uuid], r);
    else void fila.invalidateQueries({ queryKey: ["p", "marca", dados.revenda_uuid] });
    // A barra de cima de QUEM está editando também muda, se a revenda for a
    // dela — e a tela de login deste endereço, se for de um cliente dela.
    void fila.invalidateQueries({ queryKey: ["marca"] });
  };

  const salvar = useMutation({
    mutationFn: () =>
      marcaAPI.salvar(dados.revenda_uuid, {
        nome: nomeLimpo,
        ...(logo.estado === "novo" ? { logo: logo.dataURL } : logo.estado === "removido" ? { logo: null } : {}),
      }),
    onSuccess: (r) => {
      depoisDeMudar(r);
      setNome(r.padrao ? "" : r.nome);
      setLogo({ estado: "mantido" });
      setRecado(
        "Salvo. Quem abrir o webmail a partir de agora já vê a marca nova; " +
          "quem está com ele aberto, ao recarregar a página.",
      );
    },
    onError: (e) => {
      setRecado("");
      setErro(explicar(e));
    },
  });

  const voltar = useMutation({
    mutationFn: () => marcaAPI.voltarAoPadrao(dados.revenda_uuid),
    onSuccess: () => {
      depoisDeMudar(undefined);
      setNome("");
      setLogo({ estado: "mantido" });
      setRecado("Pronto: os clientes desta revenda voltam a ver a marca CDW Mail.");
    },
    onError: (e) => setErro(explicar(e)),
  });

  function escolherArquivo(arquivo: File | undefined) {
    setErro("");
    setRecado("");
    if (!arquivo) return;
    if (arquivo.type !== "image/png" && arquivo.type !== "image/jpeg") {
      setErro("Use PNG ou JPEG. SVG, GIF e WebP não são aceitos.");
      return;
    }
    if (arquivo.size > LOGO_MAXIMO) {
      setErro(`O logo vai até 256 KB; este tem ${Math.ceil(arquivo.size / 1024)} KB.`);
      return;
    }
    const leitor = new FileReader();
    leitor.onload = () => {
      const dataURL = String(leitor.result);
      const img = new Image();
      img.onload = () => {
        const { naturalWidth: l, naturalHeight: a } = img;
        if (l < LARGURA[0] || l > LARGURA[1] || a < ALTURA[0] || a > ALTURA[1]) {
          setErro(
            `O logo precisa ter de ${LARGURA[0]}×${ALTURA[0]} a ${LARGURA[1]}×${ALTURA[1]} pixels; ` +
              `este tem ${l}×${a}.`,
          );
          return;
        }
        setLogo({ estado: "novo", dataURL, arquivo: arquivo.name });
      };
      img.onerror = () => setErro("Não consegui abrir esta imagem.");
      img.src = dataURL;
    };
    leitor.readAsDataURL(arquivo);
  }

  const temLogoAgora = logo.estado === "novo" || (logo.estado === "mantido" && !dados.padrao && !!dados.logo_url);
  const iconeDaAba = previa.padrao ? "/favicon.png" : iconeDaInicial(previa.nome);

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-linha bg-white p-5">
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!nomeInvalido) salvar.mutate();
          }}
        >
          <label className="block text-sm text-tinta-fraca">
            Nome do produto
            <input
              value={nome}
              onChange={(e) => {
                setNome(e.target.value);
                setRecado("");
              }}
              maxLength={NOME_MAXIMO + 10}
              placeholder={dados.revenda_nome}
              className="mt-1 block w-full max-w-sm rounded-lg border border-linha px-3 py-2 text-sm text-tinta"
            />
            <span className="mt-1 block text-xs">
              Aparece na aba do navegador, no aplicativo de 2FA e no perfil do iPhone. Até{" "}
              {NOME_MAXIMO} caracteres.
            </span>
          </label>

          <div className="text-sm text-tinta-fraca">
            Logo
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <label className="cursor-pointer rounded-full border border-linha px-4 py-2 text-sm text-tinta hover:bg-fundo">
                {temLogoAgora ? "Trocar logo" : "Enviar logo"}
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  className="sr-only"
                  onChange={(e) => {
                    escolherArquivo(e.target.files?.[0]);
                    e.target.value = ""; // escolher o mesmo arquivo de novo tem que disparar
                  }}
                />
              </label>
              {temLogoAgora && (
                <button
                  type="button"
                  onClick={() => setLogo({ estado: "removido" })}
                  className="text-sm text-tinta-media underline-offset-2 hover:underline"
                >
                  Tirar o logo
                </button>
              )}
              {logo.estado === "novo" && <span className="text-xs">{logo.arquivo}</span>}
              {logo.estado !== "mantido" && (
                <button
                  type="button"
                  onClick={() => setLogo({ estado: "mantido" })}
                  className="text-xs text-tinta-fraca underline-offset-2 hover:underline"
                >
                  desfazer
                </button>
              )}
            </div>
            <span className="mt-1 block text-xs">
              PNG ou JPEG, até 256 KB. Fundo transparente ou branco — ele aparece sobre branco.
              Sem logo, o nome aparece escrito no lugar.
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Botao tipo="submit" ocupado={salvar.isPending} desabilitado={!sujo || !!nomeInvalido}>
              Salvar
            </Botao>
            {sujo && !nomeInvalido && <span className="text-xs text-aviso">Alterações não salvas.</span>}
            {sujo && nomeInvalido && <span className="text-xs text-perigo">{nomeInvalido}</span>}
          </div>
          {erro && <p className="text-sm text-perigo">{erro}</p>}
          {recado && <p className="text-sm text-acao">{recado}</p>}
        </form>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-medium text-tinta">Como os clientes vão ver</h2>
        <div className="space-y-3 rounded-xl border border-linha bg-fundo p-4">
          {/* A aba do navegador */}
          <div className="flex w-full max-w-xs items-center gap-2 rounded-t-lg border border-b-0 border-linha bg-white px-3 py-1.5 text-xs text-tinta">
            {iconeDaAba && <img src={iconeDaAba} alt="" className="h-4 w-4 rounded-sm" />}
            <span className="truncate">{previa.nome}</span>
          </div>
          {/* A barra de cima do webmail */}
          <div className="flex items-center gap-3 border border-linha bg-white px-3 py-2">
            <Icone nome="Menu" tamanho={20} className="text-tinta-fraca" />
            <DesenhoDaMarca marca={previa} altura={30} />
            <span className="ml-2 hidden h-8 flex-1 rounded-full bg-fundo sm:block" />
          </div>
          {/* A tela de entrar */}
          <div className="grid place-items-center rounded-lg bg-white py-6">
            <DesenhoDaMarca marca={previa} altura={38} />
            <span className="mt-4 h-8 w-48 rounded-lg border border-linha" />
          </div>
        </div>
        <p className="mt-2 text-xs text-tinta-fraca">
          A marca também vai para a página de configuração, o perfil do iPhone e o Thunderbird. Antes
          de entrar, a tela de login usa a marca do endereço: <code>webmail.dominiodocliente</code>{" "}
          mostra esta; o endereço compartilhado da plataforma mostra a CDW Mail. O endereço do
          servidor nos programas de e-mail (<code>mail.cdwmail.com.br</code>) não muda: é o
          servidor, não a marca.
        </p>
      </section>

      {!dados.padrao && (
        <section className="rounded-xl border border-linha bg-white p-5 text-sm">
          {dados.alterado_em && (
            <p className="mb-3 text-xs text-tinta-fraca">
              Alterada em {dataCurta(dados.alterado_em)}
              {dados.alterado_por ? ` por ${dados.alterado_por}` : ""}.
            </p>
          )}
          {confirmando ? (
            <div className="space-y-3">
              <p className="text-tinta">
                Os clientes desta revenda voltam a ver a marca CDW Mail, e o logo enviado é apagado.
              </p>
              <div className="flex flex-wrap gap-2">
                <Botao tom="perigo" ocupado={voltar.isPending} aoClicar={() => voltar.mutate()}>
                  Voltar à marca da plataforma
                </Botao>
                <Botao tom="neutro" aoClicar={() => setConfirmando(false)}>
                  Cancelar
                </Botao>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmando(true)}
              className="text-sm text-perigo underline-offset-2 hover:underline"
            >
              Voltar à marca da plataforma…
            </button>
          )}
        </section>
      )}
    </div>
  );
}
